import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { LotReviewMetrics, ReviewMetric } from './os-lot-review-checks';
import classes from './os-lot-reviews.module.css';

function failMessage(metric: ReviewMetric, t: TFunction) {
  if (!metric.fail) {
    return null;
  }
  if (metric.missing) {
    return t('osLotReviews.fail.missing');
  }
  if (metric.kind === 'yield') {
    return t('osLotReviews.fail.yield', {
      min: metric.rangeMin,
      max: metric.rangeMax,
    });
  }
  if (metric.kind === 'tonnage') {
    return t('osLotReviews.fail.tonnage', { max: metric.tonnageThreshold });
  }
  return t('osLotReviews.fail.area', { min: metric.areaThreshold });
}

export function OsLotReviewMetricCell({
  metric,
  referenceLabel,
}: {
  metric: ReviewMetric;
  referenceLabel?: string;
}) {
  const reference =
    metric.reference && referenceLabel
      ? `${referenceLabel} ${metric.reference}`
      : metric.reference;

  return (
    <div className={classes.metricCell}>
      <span
        className={
          metric.fail ? classes.metricCellValueFail : classes.metricCellValue
        }
      >
        {metric.value}
      </span>
      {metric.delta ? (
        <span
          className={
            metric.fail ? classes.metricCellDeltaFail : classes.metricCellDelta
          }
        >
          {metric.delta}
        </span>
      ) : null}
      {reference ? (
        <span className={classes.metricCellRef}>{reference}</span>
      ) : null}
    </div>
  );
}

export function OsLotReviewMetricPanel({
  label,
  metric,
  referenceLabel,
}: {
  label: string;
  metric: ReviewMetric;
  referenceLabel?: string;
}) {
  const { t } = useTranslation();
  const reason = failMessage(metric, t);
  const reference =
    metric.reference && referenceLabel
      ? `${referenceLabel} ${metric.reference}`
      : metric.reference;

  return (
    <div
      className={`${classes.metricPanel} ${metric.fail ? classes.metricPanelFail : ''}`}
    >
      <div className={classes.metricPanelHead}>
        <span className={classes.metricPanelLabel}>{label}</span>
        {metric.fail ? (
          <span className={classes.metricPanelKo}>
            {t('osLotReviews.indicators.fail')}
          </span>
        ) : null}
      </div>
      <div
        className={
          metric.fail ? classes.metricPanelValueFail : classes.metricPanelValue
        }
      >
        {metric.value}
      </div>
      {reference || metric.delta ? (
        <div className={classes.metricPanelCompare}>
          {reference ? (
            <span className={classes.metricPanelRef}>{reference}</span>
          ) : (
            <span />
          )}
          {metric.delta ? (
            <span
              className={
                metric.fail
                  ? classes.metricCellDeltaFail
                  : classes.metricCellDelta
              }
            >
              {metric.delta}
            </span>
          ) : null}
        </div>
      ) : null}
      {reason ? <p className={classes.metricPanelReason}>{reason}</p> : null}
    </div>
  );
}

export function OsLotReviewIssueList({
  metrics,
}: {
  metrics: LotReviewMetrics;
}) {
  const { t } = useTranslation();
  const items = [metrics.tonnage, metrics.yield, metrics.area]
    .map((metric) => failMessage(metric, t))
    .filter((item): item is string => Boolean(item));

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={classes.issueBanner}>
      <p className={classes.issueBannerTitle}>{t('osLotReviews.fail.banner')}</p>
      <ul className={classes.issueList}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}
